// Mes quittances : numéro, rubrique, montant, date, état, code de vérification (page publique) et
// PDF à télécharger quand il a été produit par la plateforme.
import { Bandeau, Bouton, EtatVide } from '@cc/ui';
import { Download, ReceiptText } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { messageErreur } from '../../../api/erreurs';
import { EtatRequete } from '../../../commun/EtatRequete';
import { date } from '../../../commun/format';
import { EnTeteCitoyen, ExigeConnexion, Liste, TexteStatut } from '../commun';
import { telechargerQuittance, useMesQuittances } from './api';
import { montant } from './logique';
import type { QuittanceUsager } from './types';
import styles from '../citoyen.module.css';

function LigneQuittance({ q, onErreur }: { q: QuittanceUsager; onErreur: (m: string) => void }) {
  const [enCours, setEnCours] = useState(false);
  return (
    <li className={`${styles.ligne} ${styles.ligneSansChevron}`}>
      <span className={styles.ligneTexte}>
        <span className={styles.ligneTitre}>
          <span className={styles.mono}>{q.numero}</span> · {montant(q.montant)}
        </span>
        <span className={styles.ligneSous}>
          {q.rubrique} · {q.collectivite.nom} · émise le {date(q.emise_le)} · code{' '}
          <Link to={`/citoyen/verification/${q.code_verification}`} className={styles.mono}>
            {q.code_verification}
          </Link>
        </span>
        <span className={styles.ligneSous}>
          <TexteStatut libelle={q.etat === 'valide' ? 'Valide' : `Annulée${q.motif_annulation ? ` : ${q.motif_annulation}` : ''}`} ton={q.etat === 'valide' ? 'ok' : 'attention'} />
          {q.mention_environnement ? ` · ${q.mention_environnement}` : ''}
        </span>
      </span>
      {q.pdf_disponible ? (
        <Bouton
          variante="secondaire"
          petit
          icone={Download}
          chargement={enCours}
          aria-label={`Télécharger la quittance ${q.numero}`}
          onClick={() => {
            setEnCours(true);
            telechargerQuittance(q.id, q.numero)
              .catch((e: unknown) => onErreur(messageErreur(e)))
              .finally(() => setEnCours(false));
          }}
        >
          PDF
        </Bouton>
      ) : (
        <span className={styles.aide}>PDF en préparation</span>
      )}
    </li>
  );
}

export function PageMesQuittances() {
  const quittances = useMesQuittances();
  const [erreur, setErreur] = useState<string | null>(null);
  const liste = [...(quittances.data ?? [])].sort((a, b) => b.emise_le.localeCompare(a.emise_le));
  return (
    <>
      <EnTeteCitoyen titre="Mes quittances" retour="/citoyen/paiements" libelleRetour="Paiements" sousTitre="Chaque quittance porte un code de vérification que toute personne peut contrôler sans voir vos données." />
      <ExigeConnexion titre="Connectez-vous pour voir vos quittances">
        <EtatRequete chargement={quittances.isPending} erreur={quittances.error} onReessayer={() => void quittances.refetch()} fonction="Vos quittances" lot="L2" lignes={5}>
          {erreur ? <Bandeau type="erreur" onFermer={() => setErreur(null)}>{erreur}</Bandeau> : null}
          {liste.length === 0 ? (
            <EtatVide titre="Aucune quittance" icone={ReceiptText}>
              <p>Une quittance est délivrée pour chaque paiement confirmé.</p>
            </EtatVide>
          ) : (
            <Liste libelle="Quittances">
              {liste.map((q) => (
                <LigneQuittance key={q.id} q={q} onErreur={setErreur} />
              ))}
            </Liste>
          )}
        </EtatRequete>
      </ExigeConnexion>
    </>
  );
}
